import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/errors/use-toast';
import type { PrivateResearchItem } from './types';

interface UsePrivateResearchDeleteProps {
  selectedResearch: PrivateResearchItem | null;
  handleDeleteResearch: (id: string) => Promise<void>;
  isDeleting: boolean;
  onDeleteComplete: () => void;
}

export const usePrivateResearchDelete = ({
  selectedResearch,
  handleDeleteResearch,
  isDeleting,
  onDeleteComplete,
}: UsePrivateResearchDeleteProps) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { toast } = useToast();

  // Handle delete research
  const handleDeleteResearchClick = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  const confirmDeleteResearch = useCallback(async () => {
    if (!selectedResearch) return;
    
    try {
      await handleDeleteResearch(selectedResearch.id);
      setShowDeleteConfirmation(false);
      onDeleteComplete();
      toast({
        title: "Success",
        description: "Private research deleted successfully",
      });
    } catch (error) {
      console.error('Failed to delete private research:', error);
      toast({
        title: "Error",
        description: "Failed to delete private research",
        variant: "destructive",
      });
    }
  }, [selectedResearch, handleDeleteResearch, onDeleteComplete, toast]);

  const cancelDeleteResearch = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, []);

  return {
    showDeleteConfirmation,
    handleDeleteResearchClick,
    confirmDeleteResearch,
    cancelDeleteResearch,
  };
}; 